/**
 * Client-side preparation for chat attachments.
 *
 * Two jobs: shrink images before they leave the device, and spot a
 * bitcoin.tax export so the user isn't left thinking they've imported it.
 */

/**
 * Longest edge the Claude API works with. Anything larger is downsampled
 * server-side before tokenisation, so uploading full-resolution costs upload
 * time and storage for detail that is discarded before the model ever sees it.
 */
const MAX_EDGE = 1568;

/** Below this, resizing costs more than it saves. */
const RESIZE_THRESHOLD_BYTES = 400 * 1024;

const RESIZABLE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

/**
 * Downscale an image to fit within MAX_EDGE, preserving aspect ratio.
 *
 * Returns the original file untouched when it's already small, isn't a
 * resizable image, or if anything goes wrong -- a failed resize should cost
 * bandwidth, never the attachment.
 *
 * GIFs are excluded deliberately: canvas re-encoding would flatten an
 * animation to a single frame, silently destroying what was attached.
 */
export const downscaleImage = async (file) => {
  if (!RESIZABLE_TYPES.has(file.type)) return file;
  if (file.size <= RESIZE_THRESHOLD_BYTES) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width, bitmap.height);

    if (longest <= MAX_EDGE) {
      bitmap.close?.();
      return file;
    }

    const scale = MAX_EDGE / longest;
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    // PNG screenshots re-encode to JPEG far smaller, but JPEG has no alpha.
    // Keeping PNG as PNG avoids turning a transparent background black.
    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, outputType, 0.9)
    );

    if (!blob || blob.size >= file.size) return file;

    return new File([blob], file.name, { type: outputType, lastModified: file.lastModified });
  } catch {
    return file;
  }
};

/**
 * Heuristic: does this CSV look like a bitcoin.tax export?
 *
 * Matters because attaching one to chat is read-only -- it does not update the
 * portfolio, unlike the Crypto upload page. Without a signpost you get a fluent,
 * accurate-sounding answer about your holdings and reasonably conclude the
 * import happened.
 *
 * Errs toward not flagging: a false positive nags about a file that has nothing
 * to do with crypto, which is more annoying than a missed hint.
 */
export const looksLikeBitcoinTaxExport = async (file) => {
  const name = (file.name || '').toLowerCase();
  if (!name.endsWith('.csv')) return false;

  try {
    // Header row only -- no need to read a multi-megabyte export to classify it.
    const header = (await file.slice(0, 2048).text()).split(/\r?\n/)[0].toLowerCase();

    const hasSymbol = /\b(symbol|currency)\b/.test(header);
    const hasVolume = /\b(volume|quantity|amount)\b/.test(header);
    const hasCost = /\b(cost|price|total)\b/.test(header);

    return hasSymbol && hasVolume && hasCost;
  } catch {
    return false;
  }
};

/** "1.4 MB" / "812 KB" — for attachment chips and the conversation footer. */
export const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/** "12.4k" — token counts get large enough that raw digits stop being readable. */
export const formatTokens = (tokens) => {
  if (!tokens) return '0';
  if (tokens < 1000) return String(tokens);
  return `${(tokens / 1000).toFixed(1)}k`;
};

/**
 * Sub-cent costs are common here and would all render as "$0.00", which reads
 * as free. Show more precision rather than rounding the number away.
 */
export const formatCost = (usd) => {
  if (usd === null || usd === undefined) return null;
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return `<$0.01`;
  return `$${usd.toFixed(2)}`;
};
